import { Alert, AlertDescription, AlertTitle } from '@/components/shadcn/alert'
import { IconAlertOutline } from '@icons/IconAlertOutline'
import { IconFolderOpenAngledOutline } from '@icons/IconFolderOpenAngledOutline'
import { Avatar, AvatarFallback } from '@/components/shadcn/avatar'
import { getInitials } from '@/lib/utils'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { format } from 'date-fns'
import { Actions } from './Actions'
import { AnimatePresence, motion } from 'motion/react'
import { useMemo, useState } from 'react'
import { NoSearchResultsDisplay } from '@/components/ui/NoSeachResultsDisplay'
import { MessagesCollaborationThreadDetails } from './Collaboration'
import { RestSchemas } from '@/contexts/QueryProvider'
import { Badge } from '@/components/shadcn/badge'

type MessagesCollaborationChannelDetails = RestSchemas['Messages.Collaboration.ChannelDetails']

const filterThreads = (threads: MessagesCollaborationThreadDetails[], searchString: string) => {
  if (!searchString) return threads
  const searchLower = searchString.toLowerCase()
  const descriptionMatch = (thread: MessagesCollaborationThreadDetails) =>
    thread.description?.toLowerCase().includes(searchLower)
  const createdByMatch = (thread: MessagesCollaborationThreadDetails) =>
    thread.createdByUser?.toLowerCase().includes(searchLower)
  const assignedToMatch = (thread: MessagesCollaborationThreadDetails) =>
    thread.assignedToUser?.toLowerCase().includes(searchLower)
  return threads.filter((thread) => {
    return descriptionMatch(thread) || createdByMatch(thread) || assignedToMatch(thread)
  })
}

export function ChannelThreadDetails({
  onSelectThread,
  channel,
  threadKey,
}: {
  onSelectThread: (threadId: number) => void
  channel?: MessagesCollaborationChannelDetails
  threadKey: string
}) {
  const { threads, id: channelId, key: channelKey } = channel || {}

  if (!channel)
    return (
      <Alert variant="default">
        <IconAlertOutline />
        <AlertTitle>Unable to find channel thread details.</AlertTitle>
        <AlertDescription>
          <p>Please verify the channel key and thread key and try again.</p>
        </AlertDescription>
      </Alert>
    )
  if (!threads) return <NoThreadsDisplay />

  return (
    <ChannelThreadDetailsContent
      threads={threads}
      onSelectThread={onSelectThread}
      channelId={channelId}
      threadKey={threadKey}
      channelKey={channelKey!}
    />
  )
}

function ChannelThreadDetailsContent({
  threads,
  onSelectThread,
  channelId,
  threadKey,
  channelKey,
}: {
  threads: MessagesCollaborationThreadDetails[]
  onSelectThread: (threadId: number) => void
  channelId?: number
  threadKey: string
  channelKey: string
}) {
  const [searchString, setSearchString] = useState('')
  const filteredThreads = useMemo(() => filterThreads(threads || [], searchString), [threads, searchString])

  return (
    <>
      <Actions
        searchString={searchString}
        setSearchString={setSearchString}
        channelId={channelId}
        threadKey={threadKey}
        channelKey={channelKey}
      />
      {/* {threads.length === 0 && <NoThreadsDisplay />} */}
      {filteredThreads.length === 0 && <NoSearchResultsDisplay />}
      <AnimatePresence mode="popLayout">
        {filteredThreads.map((thread, index) => {
          const { assignedToUser, createdByUser, date } = thread
          return (
            <motion.div
              layout
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 5 }}
              transition={{
                duration: 0.2,
                ease: 'easeOut' as const,
                delay: index * 0.1,
                layout: { duration: 0.2, ease: 'easeOut' as const, delay: 0 },
              }}
              className="flex flex-col items-stretch gap-2"
              key={thread.id}
            >
              <div
                role="button"
                onClick={() => onSelectThread(thread.id!)}
                className="relative grid grid-cols-[auto_1fr] items-center gap-8 rounded-xl bg-background p-5 shadow"
              >
                <TooltipButton tooltip={assignedToUser || '?'}>
                  <Avatar className="size-16">
                    <AvatarFallback className="bg-accent text-accent-foreground">
                      {assignedToUser ? getInitials(assignedToUser) : '?'}
                    </AvatarFallback>
                  </Avatar>
                </TooltipButton>
                <p className="text-muted-foreground">{thread.description}</p>
                <div className="absolute right-3 top-2 flex items-center gap-1">
                  <StatusBadge status={thread.status} />
                </div>
              </div>
              <div className="pr-2 text-right text-xs">
                <span className="font-light italic text-muted-foreground">Created by:</span> {createdByUser}{' '}
                {date ? ` - ${format(new Date(date), 'PPp')}` : ''}
              </div>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </>
  )
}

function NoThreadsDisplay() {
  return (
    <Alert variant="default">
      <IconFolderOpenAngledOutline />
      <AlertTitle>No threads found.</AlertTitle>
      <AlertDescription>
        This channel has no threads. You can create a new thread by clicking the "New thread" button above.
      </AlertDescription>
    </Alert>
  )
}

function StatusBadge({ status }: { status?: string | null }) {
  if (!status) return null
  if (status === 'open' || status === 'openAndMine')
    return (
      <>
        <Badge variant="secondary" className="h-4">
          Open
        </Badge>
        {status === 'openAndMine' && (
          <Badge variant="warning" className="h-4">
            Mine
          </Badge>
        )}
      </>
    )
  if (status === 'resolved')
    return (
      <Badge variant="success" className="h-4">
        Resolved
      </Badge>
    )
  return (
    <Badge variant="outline" className="h-4">
      {status}
    </Badge>
  )
}
